import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import ApiClient from '../../loopback/apiClient';
import ComboBox from '../layout/ComboBox';
import ContentBox from '../layout/ContentBox';
import GenericDialog from '../GenericDialog';
import { convertToQid } from '../../utilities/stringHelper';
import { showNotification, showErrorNotification, fetchInstitutes } from '../../redux/actions';
import { Enrollment, RootState } from '../../redux/types';
import { useAppDispatch } from '../../hooks/reduxHooks';

type ComboType = {
  label: string;
  value: string;
  userId: number;
}

type Props = {
  isOpen: boolean;
  trialId: number;
  closeDialog: () => void;
  onSuccess?: (arg1: Enrollment) => void;
  enrollmentUserId?: number;
  currentRole?: string;
}

const EnrollInternalUserDialog: React.FC<Props> = ({
  isOpen,
  trialId,
  closeDialog,
  onSuccess,
  enrollmentUserId,
  currentRole,
}: Props) => {
  const dispatch = useAppDispatch();
  const institutesById = useSelector((state: RootState) => state.institute.byId);
  const [userList, setUserList] = useState<ComboType[]>();
  const [selectedList, setSelectedList] = useState<ComboType[]>();
  const [enrollmentRole, setEnrollmentRole] = useState<string>(currentRole || 'none');
  const [headerTextId, setHeaderTextId] = useState<string>('internalTrial.enroll');
  const [proceedTextId, setProceedTextId] = useState<string>('internalTrial.enrollUser');

  useEffect(() => {
    if (!institutesById) {
      dispatch(fetchInstitutes());
    }
  }, [dispatch, institutesById]);

  useEffect(() => {
    if (!institutesById) {
      return;
    }
    ApiClient.getUsers()
      .then((users) => {
        const comboUserObj: ComboType[] = users.map((user) => {
          const { id: userId, instituteId, userCountry } = user;
          return {
            label: `${convertToQid(userId, institutesById[instituteId]?.country || userCountry)} - ${user.email}`,
            value: user.email,
            userId: user.id,
          } as ComboType;
        });
        setUserList(comboUserObj);
      });
  }, [institutesById]);

  const onSelectValue = (values: ComboType[] | null) => {
    setSelectedList(values || []);
  };

  useEffect(() => {
    if (!enrollmentUserId) {
      return;
    }
    const res = userList?.filter((user) => user.userId === enrollmentUserId);
    if (res) {
      setHeaderTextId('trials.changeEnrollmentRole');
      setProceedTextId('trials.changeEnrollmentRole');
      onSelectValue(res);
    }
  }, [enrollmentUserId, userList]);

  const showErrorMessage = (error: Error) => {
    switch (error.message) {
      case 'ALREADY_ENROLLED':
        dispatch(showErrorNotification(<FormattedMessage id="voucher.alreadyEnrolled" />));
        break;
      case 'REGISTRATION_DATE_CLOSED':
        dispatch(showErrorNotification(<FormattedMessage id="trials.registrationEnd" />));
        closeDialog();
        break;
      case 'INSTITUTE_UNAPPROVED':
        dispatch(showNotification(<FormattedMessage id="trials.instituteUnapproved" />));
        break;
      case 'RECORD_NOT_FOUND':
        dispatch(showErrorNotification(<FormattedMessage id="enrollment.recordNotFound" />));
        break;
      case 'EMAIL_NOT_VERIFIED':
        dispatch(showErrorNotification(<FormattedMessage id="enrollment.userNotVerfied" />));
        break;
      case 'USER_DEACTIVATED':
        dispatch(showErrorNotification(<FormattedMessage id="enrollment.userDeactivated" />));
        break;
      case 'NO_USER_FROM_QUIP':
        dispatch(showErrorNotification(<FormattedMessage id="enrollment.noUserFromQuip" />));
        break;
      default:
        dispatch(showErrorNotification(error.message));
    }
  };

  const enrollUser = () => {
    if (!selectedList) {
      return;
    }
    const { userId } = selectedList[0];
    ApiClient.enrollUserToTrial(trialId, userId, enrollmentRole).then((value) => {
      if (onSuccess) {
        onSuccess(value);
      }
      closeDialog();
      dispatch(showNotification(<FormattedMessage id="done" />));
    }).catch(showErrorMessage);
  };

  const changeEnrollmentRole = () => {
    if (!enrollmentUserId) {
      return;
    }
    ApiClient.changeEnrollmentRole(trialId, enrollmentUserId, enrollmentRole).then((value) => {
      if (onSuccess) {
        onSuccess(value);
      }
      closeDialog();
      dispatch(showNotification(<FormattedMessage id="done" />));
    }).catch(showErrorMessage);
  };

  return (
    <GenericDialog
      isOpen={isOpen}
      onRequestClose={closeDialog}
    >
      <ContentBox show title={<FormattedMessage id={headerTextId} />}>
        <Box
          sx={{
            height: '370px',
            overflow: 'auto',
          }}
        >
          {
            userList
            && (
              <span>
                <Typography>
                  <ComboBox<ComboType>
                    keyId="InternalUserEnroll"
                    data={userList}
                    onChange={onSelectValue}
                    selectedItems={selectedList}
                    disabled={!!enrollmentUserId}
                  />
                </Typography>
                <FormControl component="fieldset" sx={{ marginTop: '2em', marginLeft: '2em' }}>
                  <FormLabel component="legend">Enrollment Role</FormLabel>
                  <RadioGroup
                    name="enrollmentRole"
                    value={enrollmentRole}
                    onChange={(_event, value) => setEnrollmentRole(value)}
                  >
                    <FormControlLabel value="lead" control={<Radio />} label="Lead" />
                    <FormControlLabel value="panel" control={<Radio />} label="Panel" />
                    <FormControlLabel value="none" control={<Radio />} label="None" />
                  </RadioGroup>
                </FormControl>
              </span>
            )
          }
        </Box>
        <Box display="flex" columnGap={1} justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={enrollmentUserId ? changeEnrollmentRole : enrollUser}
            disabled={!selectedList && !enrollmentUserId}
          >
            <FormattedMessage id={proceedTextId} />
          </Button>
          <Button variant="contained" color="secondary" onClick={closeDialog}>
            <FormattedMessage id="genericClose" />
          </Button>
        </Box>
      </ContentBox>
    </GenericDialog>
  );
};

export default EnrollInternalUserDialog;
